import { useState, useRef, useCallback, useEffect } from "react";
import AMSListing from "../../../components/listing/AMSListing";
import { useToast } from "../../../components/toast/ToastContext";
import DeletePrompt from "../../../components/DeletePrompt";
import { useNavigate } from "react-router-dom";
import {
  useDeleteComponentMutation,
  useDuplicateComponentMutation,
  useGetAllComponentsQuery,
  useGetScopesforFieldsQuery,
} from "../../../api/formsApiSlice";
import { ActionType, Column, Component, Scope } from "../../../types/types";
import CreateComponentModal from "./CreateComponentModal";

interface ComponentsListingProps {
  onEditForm: (id: number, onSuccess: () => void) => void;
  refreshTrigger?: number;
}

const ComponentsListing = ({
  onEditForm,
  refreshTrigger,
}: ComponentsListingProps) => {
  const staticFormsColumns: Column[] = [
    { id: "id", label: "Id", isSortable: true },
    { id: "name", label: "Name", isSortable: true },
    { id: 'description', label: "Description", isSortable: false },
    { id: 'scope', label: "Scope", isSortable: false },

    { id: "isStandard", label: "Category", isFlag: true },
    { id: "account", label: "Created By", isSortable: false },
    { id: "createdAt", label: "Creation Date", isSortable: true },
  ];

  const showToast = useToast();
  const navigate = useNavigate();
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [columns, setColumns] = useState<Column[]>(staticFormsColumns);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [showNewComponentModal, setNewComponentModal] = useState(false);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [modalKey, setModalKey] = useState(0); // New key state
  const { data: scopesData = [], isLoading: scopesLoading } = useGetScopesforFieldsQuery({ isVisible: true });
  const filterGroups = [
    {
      key: 'isStandard',
      label: 'Category',
      dataType: 'select',
      options: [
        { label: 'System Components', value: true },
        { label: 'Custom Components', value: false },
      ],
    },
    {
      key: 'createdAt',
      label: 'Creation Date',
      dataType: 'date',
      options: [],
    },
    {
      key: 'scopeId',
      label: 'Scope',
      dataType: 'multi-select',
      options: scopesData?.scopes?.map((scope: Scope) => ({
        value: scope.id,
        label: scope.displayName,
      })) || [],
    },
  ];
  const lastParamsRef = useRef<{
    page: number;
    perPage: number;
    sort?: { field: string; direction: "ASC" | "DESC" };
    query?: string;
    filters: Record<string, any>;
  }>({
    page: 1,
    perPage: 1,
    filters: { isStandard: false },
  });

  const [initialFilters, setInitialFilters] = useState({
    ...lastParamsRef.current,
    query: "",
  });

  const { sort, filters, ...rest } = initialFilters;

  const params = {
    ...rest,
    ...filters,
    sort: sort?.field,
    sortDir: sort?.direction,
  };

  const {
    data: components,
    refetch,
    isLoading,
    isFetching,
  } = useGetAllComponentsQuery(
    { ...params },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [deleteComponent] = useDeleteComponentMutation();
  const [duplicateComponent] = useDuplicateComponentMutation();

  useEffect(() => {
    refetch();
  }, [refreshTrigger, refetch]);

  const confirmDelete = async () => {
    setShowDeletePrompt(false);
    try {
      await deleteComponent(deleteId!);
      refetch();
      showToast(
        "Component Deleted",
        "The selected component has been deleted.",
        "success"
      );
    } catch {
      showToast(
        "Delete Failed",
        "Failed to delete component. Please try again.",
        "error"
      );
    }
    setDeleteId(null);
  };

  const cancelDelete = () => {
    setShowDeletePrompt(false);
    setDeleteId(null);
  };

  const handleAction = useCallback(
    (
      action: ActionType,
      id: string
    ) => {
      const numericId = Number(id);

      if (action === "delete") {
        setDeleteId(numericId);
        setShowDeletePrompt(true);
        return;
      }

      if (action === "copy") {
        (async () => {
          try {
            await duplicateComponent(numericId).unwrap();
            refetch();
            showToast(
              "Component Duplicated",
              "The selected component has been successfully duplicated.",
              "success"
            );
          } catch (error) {
            showToast(
              "Duplication Failed",
              "Failed to duplicate component. Please try again.",
              "error"
            );
          }
        })();
        return;
      }

      if (action === "edit") {
        setUpdatingId(numericId);
        setModalKey(prev => prev + 1); // Update key first
        setReadOnlyMode(false);
        setNewComponentModal(true); // Then show modal
      }

      if (action === "view") {
        // Open the create/edit modal in read-only mode for viewing
        setUpdatingId(numericId);
        setModalKey(prev => prev + 1);
        setReadOnlyMode(true);
        setNewComponentModal(true);
        return;
      }

      // Optionally handle "revoke" or ignore if not needed
    },
    [refetch, duplicateComponent, showToast, navigate]
  );


  const handleModalClose = () => {
    setNewComponentModal(false);
    setUpdatingId(null);
    setReadOnlyMode(false);
  };

  const handleModalSave = () => {
    setNewComponentModal(false);
    setUpdatingId(null);
    refetch();
  };

  return (
    <>
      <AMSListing
        // showActions
        routeKey="components"
        noDataTitle="No Component Found"
        noDataSubtitle="Your Components list is currently empty. Start adding new Components to keep everything organized and easy to find."
        title="Components"
        subtitle="All components will show here"
        columns={columns.length > 0 ? columns : staticFormsColumns}
        data={components?.components.map((component: Component) => ({
          ...component,
          scope: component.scope?.displayName
        })) || []}
        totalItems={components?.totalCount}
        loading={isLoading || isFetching}
        onAction={handleAction}
        setInitialFilters={setInitialFilters}
        skeletonHeight={'42px'}
        filterGroups={filterGroups}
        defaultFilters={{ isStandard: false }}
        showFilters
      />

      {showDeletePrompt && (
        <DeletePrompt onConfirm={confirmDelete} onCancel={cancelDelete} />
      )}

      {showNewComponentModal && (
        <CreateComponentModal
          key={`component-modal-${modalKey}-${updatingId}`}
          updatingComponentId={updatingId || undefined}
          show={showNewComponentModal}
          onHide={handleModalClose}
          onSave={handleModalSave}
          readOnly={readOnlyMode}
        />
      )}
    </>
  );
};

export default ComponentsListing;